<template>

<div id="related-container">

  <LoaderComponent v-if="!loaded" />
  <div v-else>
    <BackComponent />

    <div id="top-page-navigation" class="page-navigation-wrapper" v-if="displayPageNavigation">
      <PagesComponent :pageInfo="pageInfo" :navigatePage="navigatePage" :navigatePageDirect="navigatePageDirect"></PagesComponent>
    </div>
    
    <div id="related-header" :dir="rightToLeft ? 'rtl' : 'ltr'">
      {{ this.total }} 
      {{ this.$i18n.t(i18nKey) }}
      <span class="bold">{{ returnValueInLanguage(siteLanguage, dynastyName).name }}</span>
    </div>

    <div id="results-wrapper">

      <!-- Larger picture + details of currently focused object -->
      <!-- Show this section only on larger screens -->
      <div id="results-main-object" v-if="!lessThan974 && mainObject">
        <div id="main-object-photo-container">
          <img id="main-object-image" :src="mainObject.image" />
        </div>
        <div id="main-object-description" :dir="rightToLeft ? 'rtl' : 'ltr'">
          <div id="main-object-name-date">
            <p class="bold" v-html="mainObject.name"></p>
            <p v-html="mainObject.date"></p>
          </div>
          <div id="main-object-partner-location">
            <p>{{ mainObject.location }}, {{ mainObject.country }}</p>
          </div>
        </div>
      </div>

      <!-- Thumbnail results -->
      <div id="results-container">
        <div class="result-wrapper"
          v-for="(result, index) in results" :key="result.id" 
          :class="`result-${index + 1}`">
          <div class="result">
            <!-- Hover changes "main" object display -->
            <a :href="result.url">
              <img class="result-image" 
                :src="result.image" 
                @mouseover="mainObject = result" />
            </a>
            <div class="result-details" 
              :class="{ 'details-rtl': rightToLeft }"
              :dir="rightToLeft ? 'rtl' : 'ltr'">
              <a :href="result.url">
                <span class="bold" @mouseover="mainObject = result" v-html="result.name"></span>
                <span>, {{ result.location }}, {{ result.country }}</span>
              </a>
              <p class="italic" v-html="result.date"></p>
            </div>
          </div>
        </div>
      </div>

    </div>

    <div id="bottom-page-navigation" class="page-navigation-wrapper" v-if="displayPageNavigation">
      <PagesComponent :pageInfo="pageInfo" :navigatePage="navigatePage" :navigatePageDirect="navigatePageDirect"></PagesComponent>
    </div>
    <!-- Extra padding for page when there are not multiple pages -->
    <div id="results-padding"></div>

  </div>

</div>

</template>



<script>

import {loadLocaleMessages} from "../i18n.js";
import LoaderComponent from "../components/LoaderComponent.vue";
import BackComponent from "../components/BackComponent.vue";
import PagesComponent from "../components/PagesComponent.vue";
import language from "../mixins/language.js";
import media from "../mixins/media.js";
import pageDisplay from "../mixins/pageDisplay.js";
export default {
  name: "RelatedPage",
  props: ["siteLanguage"],
  components: {
    "LoaderComponent": LoaderComponent,
    "BackComponent": BackComponent,
    "PagesComponent": PagesComponent
  },
  mixins: [language, media, pageDisplay],
  data() {
    return {
      id: this.$route.params.id,
      type: this.$route.params.type,
      lessThan974: window.matchMedia("(max-width: 974px)").matches,
      loaded: false,
      displayPageNavigation: false,
      allInfo: [],
      results: {},
        mainObject: {},
      dynastyName: [],
      pageInfo: {},
      pages: {},
      total: "",
      rightToLeft: false,
      mwnf_website: process.env.VUE_APP_MWNF_WEBSITE,
      dia_website: process.env.VUE_APP_DIA_WEBSITE,
    }
  },
  computed: {
    i18nKey: function() {
      let key;
      if (this.type === "monuments") {
        key = "islDyn_relMonFor";
      } else if (this.type === "objects") {
        key = "islDyn_RelObjFor";
      }
      return key;
    }
  },
  watch: {
    "$route" () {
      this.getResults();
    },
    "siteLanguage" () {
      this.getResultsInLanguage(this.allInfo, this.siteLanguage);
      this.setRightToLeft(this.siteLanguage);
      loadLocaleMessages(this.siteLanguage);
    },
  },
  methods: {
    // Prop used to set conditional dir
    setRightToLeft: function(language) {
      if (language === "ar") {
        this.rightToLeft = true;
      } else {
        this.rightToLeft = false;
      }
    },
    getResults: function() {
      let url = process.env.VUE_APP_API_ENDPOINT + `/dynasties/${this.id}/${this.type}`;
      if (this.$route.query.page) {
        let page = `?page=${this.$route.query.page}`;
        url += page;
      }
      return axios({ method: "GET", "url": url }).then(result => {
        let data = result.data.data;
        this.allInfo = data;
        this.getResultsInLanguage(data, this.siteLanguage);
        this.dynastyName = result.data.data[0].dynastyName;
        this.pageInfo = result.data.meta;
        this.pages = result.data.links;
        this.total = result.data.meta.total;
        this.setResultsExist();
        this.setDisplayPageNavigation();
        this.loaded = true;
      }, error => {
        this.$router.replace({ name: "error" });
        console.error(error);
      });
    },
    getResultsInLanguage: function(data, language) {
      let type = "";
      if (this.type === "monuments") {
        type = "monument";
      } else if (this.type === "objects") {
        type = "object";
      }
      let results = [];
      for (let i = 0; i < data.length; i++) {
        let entry = data[i];
        let result = {};
        // If no details are available (incomplete entry in VO),
        // skip and continue the loop
        if (entry.details.length === 0) {
          continue;
        }
        let details = this.returnValueInLanguage(language, entry.details);
        result.id = entry[`${type}Id`];
        if (details.name.slice(-1) === ".") {
          result.name = details.name.substring(0, details.name.length - 1);
        } else {
          result.name = details.name;
        }
        result.location = details.location;
        result.country = details.countryName;
        result.image = details.image;
        if (details.dateDescription && details.dateDescription.length > 150) {
          let date = details.dateDescription;
          let space = " ";
          let slice = date.substring(0, date.lastIndexOf(space, 150));
          slice = slice + "...";
          result.date = slice;
        } else {
          result.date = details.dateDescription;
        }
        result.url = details[`${type}Url`];
        results.push(result);
      }
      this.results = results;
      // Main object is set to first object initially
      this.mainObject = this.results[0];
      console.log(this.results);
    },
    setResultsExist: function() {
      if (Object.entries(this.results).length === 0) {
        this.resultsExist = false;
      } else {
        this.resultsExist = true;
      }
    },
    navigatePage: function(page) {
      let url = this.pages[page];
      return axios({ method: "GET", "url": url }).then(result => {
        this.results = result.data.items;
        this.pageInfo = result.data.meta;
        this.pages = result.data.pages;
        let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
        let updateQuery = currentQuery;
        updateQuery["page"] = this.pageInfo["current_page"];
        this.$router.push({ name: "related", query: updateQuery });
      }, error => {
        console.error(error);
      });
    },
    // Navigate directly to a page by clicking a number
    navigatePageDirect: function(page) {
      let currentQuery = JSON.parse(JSON.stringify(this.$route.query));
      let updateQuery = currentQuery;
      updateQuery["page"] = page;
      this.$router.push({ name: "related", query: updateQuery });
    },
  },
  created() {
    this.getResults();
  },
  mounted () {
    this.setRightToLeft(this.siteLanguage);
    loadLocaleMessages(this.siteLanguage);
  }
}

</script>



<style scoped lang="scss">

.bold {
  font-weight: bold;
}
.italic {
  font-style: italic;
}

#related-container {
  @include flex-center;
  flex-direction: column;
  width: 100%;
  background-color: white;
  padding: 60px;
}

.loader-container {
  min-height: 500px;
}

#languages {
  @include flex-center;
  justify-content: flex-end;
  background-color: $theme-dark;
}
  .languages-button {
    @include button-styles;
    color: $text-color;
    font-size: 95%;
    padding: 4px 8px;
  }
    .language-selected {
      background-color: $theme-light;
    }

#related-header {
  font-size: 125%;
  margin-top: 30px;
}

#results-wrapper {
  @include flex-center;
  align-items: flex-start;
  width: 100%;
}

#results-main-object {
  width: 25%;
  padding: 30px 0;
}
  #main-object-photo-container {
    margin-bottom: 15px;
  }
    #main-object-image {
      @include image-scale;
      object-fit: contain;
      max-height: 400px;
    }
  #main-object-name-date {
    margin-bottom: 15px;
  }
    
#results-container {
  width: 75%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px 30px;
  padding: 30px 0 30px 40px;
}
  .result {
    @include flex-center;
    justify-content: flex-start;
    height: 120px;
    font-size: 14px;
    color: $text-color;
  }
    .result a {
      text-decoration: none;
      color: $text-color;
    }
    .result-image {
      @include image-scale;
      object-fit: contain;
      height: 120px;
      width: 150px;
      border: 1px solid rgba($theme-light, 0.5);
    }
    .result-details {
      @include flex-center;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      height: 100%;
      padding-left: 10px;
    }
      .details-rtl {
        width: 100%;
        align-items: flex-end;
      }
      .result-details a {
        width: 100%;
        margin-bottom: 5px;
      }
      .result-details p {
        width: 100%;
      }
        .result-details a:hover {
          @include link-hover-styles;
        }

#results-padding {
  margin-bottom: 75px;
}

/* IE-only */
@media only screen and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  #results-container {
    display: -ms-grid;
    -ms-grid-columns: 1fr 30px 1fr;
  }
    .result-1,
    .result-3,
    .result-5,
    .result-7,
    .result-9,
    .result-11,
    .result-13,
    .result-15,
    .result-17,
    .result-19 {
      -ms-grid-column: 1;
    }
    .result-2,  
    .result-4,  
    .result-6,  
    .result-8,
    .result-10,
    .result-12,
    .result-14,
    .result-16,
    .result-18,
    .result-20 {
      -ms-grid-column: 3;
    }
    .result-1, .result-2 {
      -ms-grid-row: 1;
    }
    .result-3, .result-4 {
      -ms-grid-row: 2;
    }
    .result-5, .result-6 {
      -ms-grid-row: 3;
    }
    .result-7, .result-8 {
      -ms-grid-row: 4;
    }
    .result-9, .result-10 {
      -ms-grid-row: 5;
    }
    .result-11, .result-12 {
      -ms-grid-row: 6;
    }
    .result-13, .result-14 {
      -ms-grid-row: 7;
    }
    .result-15, .result-16 {
      -ms-grid-row: 8;
    }
    .result-17, .result-18 {
      -ms-grid-row: 9;
    }
    .result-19, .result-20 {
      -ms-grid-row: 10;
    }
    .result-1, .result-2,
    .result-3, .result-4,
    .result-5, .result-6,
    .result-7, .result-8,
    .result-9, .result-10,
    .result-11, .result-12,
    .result-13, .result-14,
    .result-15, .result-16,
    .result-17, .result-18,
    .result-19, .result-20 {
      margin-bottom: 15px;
    }
  .result-image-wrapper {
    position: relative;
    height: 120px;
    width: 150px;
    border: 1px solid $background-color;
  }
  .result-image {
    max-height: 100%;
    max-width: 100%;
    height: auto;
    width: auto;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    border: 1px solid transparent;
  }
  .result-details {
    width: calc(100% - 150px - 10px);
  }
    .result-details a,
    .result-details p {
      width: 100%;
    }
}

@media only screen and (max-width: 1199px) {
  #results-search-input {
    width: 60%;
  }
  .results-search-button {
    width: 16%;
  }
  .result {
    height: 175px;
  }
  .result-image {
    height: 175px;
    width: 120px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 1199px), (-ms-high-contrast: active) and (max-width: 1199px) {
  .result-image-wrapper {
    height: 175px;
    width: 120px;
  }
  .result-image {
    height: auto;
    width: auto;
  }
  .result-details {
    width: calc(100% - 120px - 10px);
  }
}

@media only screen and (max-width: 974px) {
  #results-container {
    width: 100%;
    padding-left: 0;
  }
    .result {
      height: 135px;
    }
    .result-image {
      height: 135px;
      width: 150px;
    }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 974px), (-ms-high-contrast: active) and (max-width: 974px) {
  .result-image-wrapper {
    height: 135px;
    width: 150px;
  }
  .result-image {
    height: auto;
    width: auto;
  }
  .result-details {
    width: calc(100% - 150px - 10px);
  }
}

@media only screen and (max-width: 849px) {
  // Increase height to make more room for text
  .result {
    height: 175px;
  }
  .result-image {
    height: 175px;
  }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 849px), (-ms-high-contrast: active) and (max-width: 849px) {
  .result-image-wrapper {
    height: 175px;
  }
  .result-image {
    height: auto;
  }
}

@media only screen and (max-width: 724px) {
  #results-container {
    @include flex-center;
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 20px;
  }
    .result-wrapper {
      width: 100%;
    }
      .result {
        height: 200px;
        width: 100%;
        margin-bottom: 20px;
      }
      .result a {
        width: 38%;
      }
        .result-image {
          height: 200px;
          width: 100%;
        }
      .result-details {
        width: 62%;
      }
}

/* IE-only */
@media only screen and (-ms-high-contrast: none) and (max-width: 724px), (-ms-high-contrast: active) and (max-width: 724px) {
  .result-image-wrapper {
    height: 200px;
    width: 100%;
  }
  .result-image {
    height: auto;
    width: auto;
  }
}

@media only screen and (max-width: 599px) {
  #back {
    padding: 30px 30px 0 30px;
  }
  #results-wrapper {
    padding: 0 30px;
  }
    .result-details a {
      width: 100%;
    }
}

@media only screen and (max-width: 499px) {
  #results-container {
    width: 100%;
  }
  .result {
    flex-direction: column;
    height: 250px;
  }
    .result a {
      height: 65%;
      width: 100%;
    }
      .result-image {
        height: 100%;
      }
    .result-details {
      height: 35%;
      width: 100%;
      padding-left: 0;
      margin-top: 10px;
    }
      .result-details a {
        height: auto;
      }
}

@media only screen and (max-width: 399px) {
  .result {
    height: 225px;
  }
}

</style>